import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Container } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { useMsal } from '@azure/msal-react';
import {
  canManageAccess,
  clearAdminSession,
  getAdminSignInMethod,
  isMicrosoftSignInConfigured,
  seesAmounts,
} from '../../utils/adminSession';
import { colour } from '../admin/tokens';
import ViewAsSwitch from './ViewAsSwitch';

/**
 * The staff screens' header: every screen named across the top, rather than hidden behind a menu
 * button, and the one being looked at marked.
 *
 * There is no back button. The one this replaced asked the server about a *parent's* re-enrolment
 * and then went back in the browser's history — for a member of staff that usually meant the
 * sign-in page. Every screen is one click away in the menu instead.
 */
export const adminMenu = ({ managesAccess = false, handlesMoney = false } = {}) => [
  { path: '/approver-screen', label: 'Re-enrolments' },
  { path: '/admin-manual-reenrolment', label: 'Manual re-enrolment' },
  { path: '/admin-reports', label: 'Reports' },
  { path: '/admin-bots', label: 'Bots' },
  { path: '/admin-not-returning', label: 'Not returning' },
  { path: '/admin-not-reenrolled', label: 'Not re-enrolled' },
  { path: '/admin-reminders', label: 'Reminders' },
  { path: '/admin-fee-sandbox', label: 'Fee check' },
  // Whoever handles the money, on the same footing as the re-issue clean-up below: hidden from the
  // enrolment team, who do not see amounts at all, and refused by the server for them regardless.
  //
  // Before the clean-up because it answers the earlier question. This one says, per transaction,
  // whether the money actually arrived — which is what the Tajummul family's $900 on 7 October
  // needed somebody to be able to ask.
  ...(handlesMoney ? [{ path: '/admin-payment-reconciliation', label: 'BPoint check' }] : []),
  // The accounts team's own clean-up: confirmations that went out with the wrong figures. Hidden
  // from the enrolment team, who do not see amounts at all — and the server refuses them anyway.
  ...(handlesMoney ? [{ path: '/admin-reissue-confirmations', label: 'Re-issue confirmations' }] : []),
  // Only for whoever may hand out access. The server refuses everybody else, so a link shown to
  // the wrong person would only lead to a refusal.
  ...(managesAccess ? [
    { path: '/staff-access', label: 'Staff access' },
    { path: '/admin-settings', label: 'Settings' },
  ] : []),
];

const TopheaderAdmin = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  /**
   * Signing out has to work for whichever way they signed in. Their own session is cleared first
   * either way: whatever happens next, the token on this machine is gone.
   */
  const signOut = async () => {
    const signedInWith = getAdminSignInMethod();
    clearAdminSession();

    if (signedInWith === 'microsoft' && isMicrosoftSignInConfigured()) {
      try {
        await instance.logoutRedirect();
        return;
      } catch (error) {
        // Already signed out here; ending the Microsoft session as well is a bonus, not a
        // reason to leave them stuck on the page.
        console.error('Microsoft sign-out failed:', error?.message || error);
      }
    }

    navigate('/admin-login', { replace: true });
  };

  const items = adminMenu({ managesAccess: canManageAccess(), handlesMoney: seesAmounts() });

  return (
    <Box component="header" sx={{ bgcolor: colour.surface, borderBottom: `1px solid ${colour.border}` }}>
      {/* No cap: at MUI's "xl" (1536px) the menu ran out of room on every screen, a 32" one
          included, once there were this many staff screens. */}
      <Container maxWidth={false} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/assets/logo.png`}
          alt="Australian Islamic College"
          sx={{ height: 40, flexShrink: 0, cursor: 'pointer' }}
          onClick={() => navigate('/approver-screen')}
        />

        {/* On a phone the menu scrolls sideways, where wrapping would push the page down a screen.
            From a tablet up it wraps onto a second line when the items do not fit, so every screen
            is in view: a scrollbar under a menu reads as a fault, and hides what is past it. */}
        <Box
          component="nav"
          aria-label="Staff screens"
          sx={{
            display: 'flex',
            gap: 0.5,
            flex: 1,
            minWidth: 0,
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
            overflowX: { xs: 'auto', sm: 'visible' },
            whiteSpace: 'nowrap',
          }}
        >
          {items.map((item) => {
            const current = pathname.startsWith(item.path);
            return (
              <Button
                key={item.path}
                onClick={() => navigate(item.path)}
                aria-current={current ? 'page' : undefined}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: current ? 700 : 500,
                  color: current ? colour.primaryDeep : colour.body,
                  bgcolor: current ? colour.primaryTint : 'transparent',
                  borderRadius: '8px',
                  px: 1.5,
                  flexShrink: 0,
                  '&:hover': { bgcolor: current ? colour.primaryTint : colour.panel },
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Box>

        <ViewAsSwitch />

        <Button
          onClick={signOut}
          startIcon={<LogoutIcon />}
          sx={{ textTransform: 'none', fontSize: '14px', color: colour.body, flexShrink: 0 }}
        >
          Sign out
        </Button>
      </Container>
    </Box>
  );
};

export default TopheaderAdmin;
